import React, { useEffect } from "react";
import SplashScreen from "@/components/SplashScreen";
import { useClientAccess } from "@/contexts/client-access-context";
import { useParams, useNavigate, useLocation } from "react-router-dom";

interface ClientProtectedRouteProps {
  children: React.ReactNode;
}

export const ClientProtectedRoute: React.FC<ClientProtectedRouteProps> = ({
  children,
}) => {
  const { clientId, projectId } = useParams<{
    clientId: string;
    projectId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isAuthenticated,
    projectId: authProjectId,
    clientId: authClientId,
    isLoading,
  } = useClientAccess();

  useEffect(() => {
    // If not authenticated, redirect to client access gate
    if (!isAuthenticated) {
      const searchParams = new URLSearchParams();
      if (projectId) searchParams.set("projectId", projectId);
      if (clientId) searchParams.set("clientId", clientId);

      // Store the intended destination
      searchParams.set("redirect", location.pathname);

      navigate(`/client-access?${searchParams.toString()}`);
      return;
    }

    // If authenticated but for a different project, redirect to correct authentication
    if (
      isAuthenticated &&
      (authProjectId !== projectId || authClientId !== clientId)
    ) {
      const searchParams = new URLSearchParams();
      if (projectId) searchParams.set("projectId", projectId);
      if (clientId) searchParams.set("clientId", clientId);
      searchParams.set("redirect", location.pathname);

      navigate(`/client-access?${searchParams.toString()}`);
      return;
    }
  }, [
    isAuthenticated,
    authProjectId,
    authClientId,
    projectId,
    clientId,
    navigate,
    location.pathname,
  ]);

  // Show loading while checking authentication
  if (isLoading) {
    return <SplashScreen />;
  }

  // If not authenticated, don't render children (will redirect)
  if (!isAuthenticated) {
    return <SplashScreen />;
  }

  // If authenticated for wrong project, don't render children (will redirect)
  if (authProjectId !== projectId || authClientId !== clientId) {
    return <SplashScreen />;
  }

  // All checks passed, render the protected content
  return <>{children}</>;
};
